import { FC, ReactNode, useEffect, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { serverUpsertPageAccessData } from "api/api-server-actions";
import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import {
  getLateSubmissionDueDate,
  isInLateSubmissionWindow,
  isLateSubmissionDueDateIndefinite,
  isPastSubmissionDueDate,
} from "components/materials/page/helpers";
import { PageAccessDataType, PageType } from "components/server-types";
import { monthDayAtTime } from "i18n/helpers";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import {
  CheckIcon,
  CircleCheckFilledIcon,
  CircleCrossIcon,
  LongFormPencilIcon,
  RepeatIcon,
} from "mds/icons";
import { cn } from "mds/utils/cn";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionUserId,
  selectPageAccessDataForSectionUser,
  selectPageGroupForReadyUser,
  selectPageGroupsWithUsersByPageId,
  selectSelectedPageTabVariant,
  selectUserSubmissionsByPageId,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { isGroupPage, isIndividualPage, isStaticPage } from "utils/page-utils";
import { useQueryParamsActions } from "utils/routing";

interface StudentSubmitButtonProps {
  selectedPage: PageType;
}

export const StudentSubmitButton: FC<StudentSubmitButtonProps> = ({ selectedPage }) => {
  const dispatch = useAppDispatch();
  const { deleteQueryParams } = useQueryParamsActions();
  const [isSaving, setIsSaving] = useState(false);

  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const group = useAppSelector((s) => selectPageGroupForReadyUser(s, selectedPage?.id));
  const isStaticContent = isStaticPage(selectedPage);
  const isGrouped = isGroupPage(selectedPage);
  const isIndividual = isIndividualPage(selectedPage);
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForSectionUser(s, selectedPage?.id, sectionUserId),
  );
  const hasDueDate = Boolean(selectedPage.due_at);
  const isPastDueDate = !isStaticContent && isPastSubmissionDueDate(selectedPage);
  const isWithinLateSubmissionPeriod = isInLateSubmissionWindow(selectedPage);
  const isDone = Boolean(pageAccessData?.completed_at);
  const showEditButton = isDone || tabVariant === "my_submission";
  const isDisabled = (isGrouped && !group) || isSaving;
  const disabledKey = isSaving ? "page_view.saving" : "page_view.group_mark_done_missing_group";

  if (!selectedPage) {
    return null;
  }

  const toggleDoneStatus = async () => {
    // If we are on the Submission tab, switch to the Workspace tab ("my_draft")
    if (tabVariant === "my_submission") {
      deleteQueryParams([SUBMISSION_ID_QUERY_PARAM]);
      dispatch(viewStateActions.setSelectedPageTabVariant("my_draft"));

      // If the page is not done, return without updating the page access data
      // (because we want the page to remain in edit mode)
      if (!isDone) {
        return;
      }
    }

    const dataProps: Partial<PageAccessDataType> = isDone
      ? { completed_at: null, completed_by_id: null }
      : { completed_at: new Date().toISOString(), completed_by_id: sectionUserId };

    if (pageAccessData) {
      storeApi.page_access_data.partial_update({
        id: pageAccessData.id,
        ...dataProps,
      });
    } else {
      if (isGrouped) {
        dataProps.page_group_id = group?.id;
      } else if (isIndividual || isStaticContent) {
        // Static pages are treated as individual pages for the purpose of marking pages complete.
        dataProps.section_user_id = sectionUserId;
      }

      if (isSaving) {
        return;
      }

      setIsSaving(true);

      try {
        await serverUpsertPageAccessData({
          page_id: selectedPage.id,
          section_id: selectedPage.section_id,
          ...dataProps,
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (isStaticContent) {
    return (
      <SubmitButtonForStaticPage
        disabled={isSaving}
        page={selectedPage}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  // If the due date has passed and late submissions are not allowed,
  // the submit button behaves the same as for a page with no due date.
  if (!hasDueDate || (isPastDueDate && !isWithinLateSubmissionPeriod)) {
    return (
      <SubmitButtonForPageWithNoDueDate
        disabled={isDisabled}
        disabledTooltip={t(disabledKey)}
        isGrouped={isGrouped}
        page={selectedPage}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  if (isPastDueDate && isWithinLateSubmissionPeriod) {
    return (
      <SubmitButtonForPageWithPastDueDate
        isSaving={isSaving}
        page={selectedPage}
        pageAccessData={pageAccessData}
        setIsSaving={setIsSaving}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  return (
    <SubmitButtonForPageWithFutureDueDate
      disabled={isDisabled}
      disabledTooltip={t(disabledKey)}
      isGrouped={isGrouped}
      page={selectedPage}
      showEditButton={showEditButton}
      toggleDoneStatus={toggleDoneStatus}
    />
  );
};

interface SubmitButtonForStaticPageProps {
  disabled: boolean;
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForStaticPage: FC<SubmitButtonForStaticPageProps> = ({
  disabled,
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  return (
    <RoundedButton
      disabled={disabled}
      eventName={
        showEditButton ? "Student Page - Edit response" : "Student Page - I'm / We're Done"
      }
      eventProps={{ pageTitle: page.title, pageId: page.id }}
      kind={showEditButton ? "primary" : "accent"}
      onClick={toggleDoneStatus}
    >
      <IconText
        iconStart={showEditButton ? <CircleCrossIcon /> : <CheckIcon />}
        text={showEditButton ? t("page_view.mark_incomplete") : t("page_view.mark_complete")}
      />
    </RoundedButton>
  );
};

interface SubmitButtonForPageWithNoDueDateProps {
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  isGrouped: boolean;
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForPageWithNoDueDate: FC<SubmitButtonForPageWithNoDueDateProps> = ({
  disabled,
  disabledTooltip,
  isGrouped,
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  const key = showEditButton
    ? isGrouped
      ? "page_view.group_edit_page"
      : "page_view.edit_page"
    : "page_view.done_editing";

  return (
    <Tooltip content={disabled && disabledTooltip}>
      {(tooltipAnchorProps) => (
        <RoundedButton
          aria-disabled={disabled}
          eventName={
            showEditButton ? "Student Page - Edit response" : "Student Page - I'm / We're Done"
          }
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          kind={showEditButton ? "primary" : "accent"}
          onClick={toggleDoneStatus}
          {...tooltipAnchorProps}
        >
          <IconText
            iconStart={showEditButton ? <LongFormPencilIcon /> : <CheckIcon />}
            text={t(key)}
          />
        </RoundedButton>
      )}
    </Tooltip>
  );
};

interface SubmitButtonIconProps {
  disabled?: boolean;
  isHovering?: boolean;
}

export const SubmitButtonIcon: FC<SubmitButtonIconProps> = ({ disabled, isHovering }) => {
  return (
    <CircleCheckFilledIcon
      className={cn({
        "text-green": !disabled && !isHovering,
        "text-black-tint-70": disabled,
        "text-green-tint-20": isHovering,
      })}
    />
  );
};

interface SubmitButtonForPageWithFutureDueDateProps {
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  isGrouped: boolean;
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForPageWithFutureDueDate: FC<SubmitButtonForPageWithFutureDueDateProps> = ({
  disabled,
  disabledTooltip,
  isGrouped,
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageGroups = useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, page.id)) || [];
  const userPageGroup = pageGroups.find((pageGroup) =>
    pageGroup.page_group_users.find(
      (pageGroupUser) => pageGroupUser.section_user_id === sectionUserId && pageGroupUser.is_ready,
    ),
  );
  const [isHovering, setIsHovering] = useState(false);
  const [hasEditBeenClicked, setHasEditBeenClicked] = useState(false);

  if (!page || !page.due_at) {
    return null;
  }

  const { month, day, time } = monthDayAtTime(page.due_at);
  const haveSubmissionsBeenCollected = Boolean(page.submissions_last_collected_at);
  const isBlueVariant = !haveSubmissionsBeenCollected && hasEditBeenClicked;

  const editButtonTextKey = haveSubmissionsBeenCollected
    ? isGrouped
      ? "page_view.group_edit_page"
      : "page_view.edit_page"
    : isGrouped
      ? "page_view.group_edit_submission"
      : "page_view.edit_submission";

  const submitButtonTextKey = haveSubmissionsBeenCollected
    ? isGrouped
      ? "page_view.group_resubmit"
      : "page_view.individual_resubmit"
    : hasEditBeenClicked
      ? "page_view.done_editing"
      : isGrouped
        ? "page_view.group_submit"
        : "page_view.individual_submit";

  const onEdit = () => {
    setHasEditBeenClicked(true);
    toggleDoneStatus();
  };

  const onSubmit = () => {
    setHasEditBeenClicked(false);
    toggleDoneStatus();
  };

  const onResubmit = () => {
    const options = isGrouped
      ? { page_group_id: userPageGroup?.id }
      : { section_user_id: sectionUserId };
    storeApi.submissions.create({ page_id: page.id, section_id: page.section_id, ...options });

    onSubmit();
  };

  if (showEditButton) {
    return (
      <Tooltip
        content={
          !page.submissions_last_collected_at &&
          t("page_view.response_will_be_submitted", { time, month, day })
        }
      >
        {(tooltipAnchorProps) => (
          <RoundedButton
            aria-disabled={disabled}
            eventName="Student Page - Edit response"
            eventProps={{ pageTitle: page.title, pageId: page.id }}
            kind="primary"
            onClick={onEdit}
            {...tooltipAnchorProps}
          >
            <IconText iconStart={<LongFormPencilIcon />} text={t(editButtonTextKey)} />
          </RoundedButton>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip content={disabled && disabledTooltip}>
      {(tooltipAnchorProps) => (
        <RoundedButton
          aria-disabled={disabled}
          eventName="Student Page - I'm / We're Done"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          kind={isBlueVariant ? "accent" : "success"}
          onClick={() => {
            if (haveSubmissionsBeenCollected) {
              onResubmit();
            } else {
              onSubmit();
            }
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          {...tooltipAnchorProps}
        >
          <IconText
            iconStart={
              isBlueVariant ? (
                <CheckIcon />
              ) : haveSubmissionsBeenCollected ? (
                <RepeatIcon />
              ) : (
                <SubmitButtonIcon disabled={disabled} isHovering={isHovering} />
              )
            }
            text={t(submitButtonTextKey)}
          />
        </RoundedButton>
      )}
    </Tooltip>
  );
};

export interface SubmitButtonForPageWithPastDueDateProps {
  isSaving: boolean;
  page: PageType;
  pageAccessData?: PageAccessDataType | null;
  setIsSaving: (isSaving: boolean) => void;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

export const SubmitButtonForPageWithPastDueDate: FC<SubmitButtonForPageWithPastDueDateProps> = ({
  isSaving,
  page,
  pageAccessData,
  setIsSaving,
  showEditButton,
  toggleDoneStatus,
}) => {
  const isWithinLateSubmissionPeriod = isInLateSubmissionWindow(page);

  const { value: isEnabled, setTrue: enable, setFalse: disable } = useBoolean(true);

  const sectionUserId = useAppSelector(selectCurrentSectionUserId);

  const isGrouped = isGroupPage(page);
  const pageGroups = useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, page.id)) || [];
  const userPageGroup = pageGroups.find((pageGroup) =>
    pageGroup.page_group_users.find(
      (pageGroupUser) => pageGroupUser.section_user_id === sectionUserId && pageGroupUser.is_ready,
    ),
  );

  const submissions = useListSelector((s) => selectUserSubmissionsByPageId(s, page.id));
  const hasLateSubmission =
    page.due_at && submissions.some((s) => new Date(s.created_at) > new Date(page.due_at!));

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isEnabled) {
      timeout = setTimeout(() => {
        enable();
      }, 5000); // Re-enable Submit Late button after 5 seconds.
    }

    return () => clearTimeout(timeout);
  }, [isEnabled, enable]);

  const options = isGrouped
    ? { page_group_id: userPageGroup?.id }
    : { section_user_id: sectionUserId };

  const onSubmitLate = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    disable();

    storeApi.submissions.create({ page_id: page.id, section_id: page.section_id, ...options });
    try {
      if (pageAccessData) {
        await storeApi.page_access_data.partial_update({
          id: pageAccessData.id,
          completed_at: new Date().toISOString(),
          completed_by_id: sectionUserId,
        });
      } else {
        await serverUpsertPageAccessData({
          page_id: page.id,
          section_id: page.section_id,
          completed_at: new Date().toISOString(),
          completed_by_id: sectionUserId,
          ...options,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const [isHovering, setIsHovering] = useState(false);

  if (!page || !page.due_at) {
    return null;
  }

  // TODO: use date formatter for this
  const { month, day, time } = monthDayAtTime(
    isLateSubmissionDueDateIndefinite(page)
      ? ""
      : (getLateSubmissionDueDate(page) as Date).toString(),
  );

  const tooltipContent = isWithinLateSubmissionPeriod
    ? t("page_worksheet_header.due_date_has_passed", {
        context: isLateSubmissionDueDateIndefinite(page) ? "indefinite_due_date" : "late_due_date",
        month,
        day,
        time,
      })
    : t("page_worksheet_header.late_due_date");

  if (showEditButton) {
    return (
      <RoundedButton
        disabled={isSaving}
        eventName="Student Page - Edit response"
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind="primary"
        onClick={toggleDoneStatus}
      >
        <IconText
          iconStart={<LongFormPencilIcon />}
          text={t(isGrouped ? "page_view.group_edit_submission" : "page_view.edit_submission")}
        />
      </RoundedButton>
    );
  }

  const submitButtonTextKey = hasLateSubmission
    ? isGrouped
      ? "page_view.group_resubmit_late"
      : "page_view.individual_resubmit_late"
    : isGrouped
      ? "page_view.group_submit_late"
      : "page_view.individual_submit_late";

  // TODO: If the late due date passes, this will likely not update the disable state in real time for the student.
  return (
    <Tooltip content={tooltipContent}>
      {(tooltipAnchorProps) => (
        <RoundedButton
          aria-disabled={!isEnabled || isSaving}
          eventName="Student Page - Submit late"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          kind="success"
          onClick={onSubmitLate}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          {...tooltipAnchorProps}
        >
          <IconText
            iconStart={
              hasLateSubmission ? (
                <RepeatIcon />
              ) : (
                <SubmitButtonIcon disabled={!isEnabled || isSaving} isHovering={isHovering} />
              )
            }
            text={t(submitButtonTextKey)}
          />
        </RoundedButton>
      )}
    </Tooltip>
  );
};
